import CalendarInlineComposeViewState from './store/schema/CalendarInlineComposeViewState';
import CalendarRollUpViewState from './store/schema/CalendarRollUpViewState';
import ConversationReadingPaneViewState from './store/schema/ConversationReadingPaneViewState';
import ItemPartViewState from './store/schema/ItemPartViewState';
import ItemReadingPaneViewState from './store/schema/ItemReadingPaneViewState';
import ItemViewState from './store/schema/ItemViewState';
import QuotedBodyViewState from './store/schema/QuotedBodyViewState';
import { getFocusedItemPart } from './utils/focusedItemPartUtils';
import { getItemsCountInCollapsedItemsRollUp } from './utils/rollUp/collapsedItemsRollUpUtils';
import { ClientItemId } from 'owa-client-ids';
import { ActionSource } from 'owa-mail-store';

export default {
    // Performance data of load conversation reading pane
    RPPerfLoadConversationReadingPane: {
        datapoint: { name: 'RPPerfLoadConversationReadingPane' },
    },

    // Performance data of load item reading pane
    RPPerfLoadItemReadingPane: {
        datapoint: {
            name: 'RPPerfLoadItemReadingPane',
        },
    },

    // Counts the number of times users collapse the quoted text
    // CustomData: 1 - isQuotedTextChanged
    //             2 - isExpanded
    RPCountExpCollQuotedText: {
        datapoint: {
            name: 'RPCountExpCollQuotedText',
            customData: (
                viewState: QuotedBodyViewState,
                itemId: string,
                isQuotedTextChanged: boolean
            ) => {
                return [!!isQuotedTextChanged ? 1 : 0, !viewState.isExpanded];
            },
        },
    },

    // Count the number of times user expand/collapse item part
    // CustomData: 1 - itemPartStateChange:
    //                      "SC" - select to collapse,
    //                      "ES" - expand to select,
    //                      "CS" - collapse to select
    //                      "EC" - expand to collapse
    //                      "CE" - collapse to expand
    // CustomData: 2 - fromKeyboard
    RPCountExpCollItemPart: {
        datapoint: {
            name: 'RPCountExpCollItemPart',
            customData: (itemPart: ItemPartViewState, fromKeyboard: boolean, canFocus: boolean) => {
                let state: string;
                if (fromKeyboard || canFocus) {
                    if (itemPart.isExpanded) {
                        state = 'EC';
                    } else {
                        state = 'CE';
                    }
                } else {
                    if (itemPart == getFocusedItemPart()) {
                        state = 'SC';
                    } else if (itemPart.isExpanded) {
                        state = 'ES';
                    } else {
                        state = 'CS';
                    }
                }

                return [state, fromKeyboard];
            },
        },
    },

    // Count the number of times user expand/collapse subject
    // CustomData: 1 - isExpanded
    RPCountExpCollSubject: {
        datapoint: {
            name: 'RPCountExpCollSubject',
            customData: (
                viewState: ConversationReadingPaneViewState | ItemReadingPaneViewState
            ) => {
                return [viewState && !viewState.isSubjectExpanded];
            },
        },
    },

    // Counts the number of user click to load more
    RPCountLoadMore: { datapoint: { name: 'RPCountLoadMore' } },

    RPCountReParentingEvent: {
        datapoint: {
            name: 'RPCountReParentingEvent',
            customData: (
                draftHasBcc: boolean,
                recipientsOnServer: boolean,
                recipientsAdded: boolean,
                recipientsDropped: boolean
            ) => [draftHasBcc, recipientsOnServer, recipientsAdded, recipientsDropped],
        },
    },

    RPCountInfoBar: {
        datapoint: {
            name: 'RPCountInfoBar',
            customData: (count: number) => [count],
        },
    },

    agendaMailFeedbackDatapoint: {
        datapoint: {
            name: 'AgendaMailFeedback',
            customData: (feedbackType: string): [any] => [feedbackType],
        },
    },

    RPCountExpCollItemsRollUp: {
        datapoint: {
            name: 'RPCountExpCollItemsRollUp',
            customData: (viewState: ConversationReadingPaneViewState, isAuto: boolean) => {
                return [getCountBucket(getItemsCountInCollapsedItemsRollUp(viewState)), isAuto];
            },
        },
    },

    RPCountRenderCollItemsRollUp: {
        datapoint: {
            name: 'RPCountRenderCollItemsRollUp',
            customData: (viewState: ConversationReadingPaneViewState) => [
                getCountBucket(getItemsCountInCollapsedItemsRollUp(viewState)),
            ],
        },
    },

    RPCountExpCollAllItemParts: {
        datapoint: {
            name: 'RPCountExpCollAllItemParts',
            customData: (shouldExpand: boolean, isFromShortcut: boolean) => [
                shouldExpand,
                isFromShortcut,
            ],
        },
    },

    PrintItemDatapoint: {
        datapoint: {
            name: 'PrintItem',
            actionSource: (itemId: ClientItemId, actionSource: ActionSource) => actionSource,
        },
    },

    ReplyByMeeting: {
        datapoint: {
            name: 'ReplyByMeeting',
            actionSource: (
                referenceItemId: string,
                calendarInlineComposeViewState?: CalendarInlineComposeViewState,
                shouldSend?: boolean,
                actionSource?: ActionSource
            ) => actionSource,
        },
    },

    ToggleUndoDarkMode: {
        datapoint: {
            name: (viewState: ItemViewState) => {
                return viewState.undoDarkMode ? 'ToggleDarkModeOn' : 'ToggleDarkModeOff';
            },
            actionSource: (viewState: ItemViewState, actionSource: ActionSource) => actionSource,
        },
    },

    InlineTranslationSuggestionShown: {
        datapoint: {
            name: 'InlineTranslationSuggestionShown',
            cosmosOnlyData: (itemId: string) => itemId,
        },
    },

    RPInfoBarEmptyCase: {
        datapoint: {
            name: 'RPInfoBarEmptyCase',
            customData: (messageId: string) => [messageId],
        },
    },

    RPProcessSmimeItem: {
        datapoint: {
            name: 'RPProcessSmimeItem',
        },
    },

    RPConversationViewSmimeItemPopoutClick: {
        datapoint: {
            name: 'RPConversationViewSmimeItemPopoutClick',
        },
    },

    ToggleIsCalendarRollUpExpanded: {
        datapoint: {
            name: (viewState: CalendarRollUpViewState) => {
                return viewState.isExpanded ? 'RPCollapseCalendarRollUp' : 'RPExpandCalendarRollUp';
            },
        },
    },

    ToggleCalendarRollUpItemPartExpanded: {
        datapoint: {
            name: (viewState: ItemPartViewState) => {
                return viewState.isExpanded
                    ? 'RPCollapseCalendarRollUpItemPart'
                    : 'RPExpandCalendarRollUpItemPart';
            },
        },
    },

    RPCacheStatus: {
        datapoint: {
            name: 'RPCacheStatus',
            customData: (
                hasValidConversationCache: boolean,
                hasOpened: boolean,
                OpenedCount: number
            ) => {
                return {
                    hasValidConversationCache,
                    hasOpened,
                    OpenedCount,
                };
            },
        },
    },

    RPMailStoreItemsCount: {
        datapoint: {
            name: 'RPMailStoreItemsCount',
            customData: (count: number) => {
                return {
                    count,
                };
            },
        },
    },
};

export function getCountBucket(count: number): number {
    return Math.min(Math.floor(count / 2), 24);
}
